import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, LogOut, Menu, X } from 'lucide-react';
import { ConfirmDialog } from './ui';

export default function Navbar() {
  const { isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  // Logged-in users get the Exchange that lives inside the dashboard shell (so they never
  // leave the reserved area); everyone else gets the public map.
  const exchangePath = isAuthenticated ? '/dashboard/exchange' : '/exchange';

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // When clicking logo or Home while already on "/", smooth-scroll to top instead of navigating
  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">

            {/* Wordmark */}
            <Link to="/" onClick={handleHomeClick} className="font-display text-3xl text-stone-950 tracking-tight">
              MatchMarble
            </Link>

            {/* Desktop center links */}
            <div className="hidden md:flex items-center gap-14">
              <Link to="/" onClick={handleHomeClick}
                className={`text-base tracking-wide transition-colors ${isActive('/') ? 'text-stone-950 font-medium' : 'text-stone-500 hover:text-stone-900'}`}>
                {t('nav.home')}
              </Link>
              <Link to={exchangePath}
                className={`text-base tracking-wide transition-colors ${isActive(exchangePath) ? 'text-stone-950 font-medium' : 'text-stone-500 hover:text-stone-900'}`}>
                Exchange
              </Link>
              <a href="/#how-it-works"
                className="text-base tracking-wide text-stone-500 hover:text-stone-900 transition-colors">
                {t('nav.howItWorks')}
              </a>
              {isAuthenticated && (
                <Link to="/dashboard"
                  className={`text-base tracking-wide transition-colors flex items-center gap-1.5 ${location.pathname.startsWith('/dashboard') ? 'text-stone-950 font-medium' : 'text-stone-500 hover:text-stone-900'}`}>
                  <LayoutDashboard size={16} />{t('nav.dashboard')}
                </Link>
              )}
            </div>

            {/* Desktop right */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <button onClick={() => setConfirmSignOut(true)}
                  className="flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-700 transition-colors">
                  <LogOut size={14} />
                </button>
              ) : (
                <>
                  <Link to="/login"
                    className="text-sm text-stone-600 hover:text-stone-950 transition-colors">
                    {t('nav.vendorLogin')}
                  </Link>
                  <Link to="/register"
                    className="text-sm bg-stone-950 text-white px-4 py-2 rounded-lg hover:bg-stone-800 transition-colors">
                    {t('nav.register')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(true)} className="p-2 rounded-lg hover:bg-stone-100 md:hidden">
              <Menu size={20} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col drawer-enter">
            <div className="flex items-center justify-between p-4 border-b border-stone-100">
              <span className="font-display text-base text-stone-950">MatchMarble</span>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-stone-100">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 p-4 space-y-1">
              <Link to="/" onClick={handleHomeClick}
                className={`block px-3 py-2.5 text-sm rounded-lg transition-colors ${isActive('/') ? 'bg-stone-100 text-stone-950 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}>
                {t('nav.home')}
              </Link>
              <Link to={exchangePath}
                className={`block px-3 py-2.5 text-sm rounded-lg transition-colors ${isActive(exchangePath) ? 'bg-stone-100 text-stone-950 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}>
                Exchange
              </Link>
              <a href="/#how-it-works" onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 text-sm text-stone-600 hover:bg-stone-50 rounded-lg transition-colors">
                {t('nav.howItWorks')}
              </a>
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard"
                    className={`block px-3 py-2.5 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/dashboard') ? 'bg-stone-100 text-stone-950 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}>
                    {t('nav.dashboard')}
                  </Link>
                  <button onClick={() => { setMobileOpen(false); setConfirmSignOut(true); }}
                    className="block w-full text-left px-3 py-2.5 text-sm text-stone-400 hover:bg-stone-50 rounded-lg transition-colors">
                    {t('dashboardNav.signOut', 'Sign out')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login"
                    className={`block px-3 py-2.5 text-sm rounded-lg transition-colors ${isActive('/login') ? 'bg-stone-100 text-stone-950 font-medium' : 'text-stone-600 hover:bg-stone-50'}`}>
                    {t('nav.vendorLogin')}
                  </Link>
                  <Link to="/register"
                    className="block px-3 py-2.5 text-sm bg-stone-950 text-white rounded-lg text-center mt-2">
                    {t('nav.register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmSignOut}
        title={t('auth.signOutConfirmTitle', 'Sign out?')}
        message={t('auth.signOutConfirmMessage', 'Are you sure you want to sign out of your account?')}
        confirmLabel={t('auth.signOutConfirmYes', 'Sign out')}
        cancelLabel={t('auth.signOutConfirmNo', 'Cancel')}
        onConfirm={() => { setConfirmSignOut(false); logout(); }}
        onCancel={() => setConfirmSignOut(false)}
      />
    </>
  );
}
